import type { AuthUser, ImpersonationKind } from '../stores/auth.store';

/**
 * Section 13B.2 handoff, shared by super-admin takeover and "Manage as".
 *
 * The originating tab stashes the impersonation token in localStorage under a
 * random key and opens /auth/callback?takeover_token=<key> in a new tab. That
 * tab reads + immediately clears the entry (consumeImpersonationHandoff). The
 * window of exposure is the time between window.open() and the new tab's
 * first JS tick — usually <100ms — with a 5s safety-net removal in case the
 * new tab never picks it up (popup blocker, user-cancelled, etc.).
 *
 * The key prefix and `takeoverBy` field are kept from the original takeover
 * implementation so the payload stays backwards compatible.
 */

const HANDOFF_KEY_PREFIX = 'engage-takeover-';
const HANDOFF_TTL_MS = 5000;

export interface ImpersonationHandoff {
  token: string;
  user: AuthUser;
  /** Email of the person doing the impersonating (shown in the banner). */
  takeoverBy: string | null;
  /** Absent on payloads written before Manage as existed — treat as 'takeover'. */
  kind?: ImpersonationKind;
  expiresAt?: string | null;
}

export function openImpersonationTab({
  token,
  user,
  actorEmail,
  kind,
  expiresAt,
}: {
  token: string;
  user: AuthUser;
  actorEmail: string | null;
  kind: ImpersonationKind;
  expiresAt?: string | null;
}): void {
  const handoffKey = `${HANDOFF_KEY_PREFIX}${crypto.randomUUID()}`;
  const payload: ImpersonationHandoff = {
    token,
    user,
    takeoverBy: actorEmail,
    kind,
    expiresAt: expiresAt ?? null,
  };
  window.localStorage.setItem(handoffKey, JSON.stringify(payload));

  window.setTimeout(() => window.localStorage.removeItem(handoffKey), HANDOFF_TTL_MS);

  window.open(`/auth/callback?takeover_token=${encodeURIComponent(handoffKey)}`, '_blank', 'noopener');
}

/**
 * Read a handoff and remove it from storage in the same tick. Returns null
 * when the entry has expired or was already consumed; throws on a malformed
 * payload.
 */
export function consumeImpersonationHandoff(handoffKey: string): ImpersonationHandoff | null {
  const raw = window.localStorage.getItem(handoffKey);
  // Clear immediately — minimises the window where another tab in the same
  // browser could grab the token.
  window.localStorage.removeItem(handoffKey);
  if (!raw) return null;
  return JSON.parse(raw) as ImpersonationHandoff;
}
